import React from 'react'
import { ArrowDown, ArrowUpRight, Play, Sparkles } from 'lucide-react'
import Atmosphere from './Atmosphere'
import TiltCard from './TiltCard'
import { usePrefersReducedMotion } from '../hook/usePrefersReducedMotion'

const Hero = () => {
  const reduced = usePrefersReducedMotion()

  const scrollToCreations = () => {
    document.getElementById('Creation')?.scrollIntoView({ behavior: 'smooth' })
  }

  const openContact = () => window.dispatchEvent(new CustomEvent('pdv:open-contact'))

  const chips = ['Blender', 'Unreal Engine', 'Unity', 'Suite Adobe', 'Motion capture', 'IA générative']

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden flex items-center pt-32 lg:pt-40">
      {/* Voiles dégradés pour la profondeur */}
      <div
        className="absolute inset-0 -z-10"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(180deg, rgba(10,2,24,0.72) 0%, rgba(19,6,44,0.78) 45%, rgba(10,2,24,0.97) 100%)',
        }}
      />
      <div
        className="absolute inset-0 -z-10"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(80% 60% at 75% 35%, transparent 0%, rgba(10,2,24,0.45) 100%)',
        }}
      />
      <Atmosphere variant="top" />

      <div className="container-pdv relative z-10 pt-4 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Colonne texte */}
          <div className="lg:col-span-7">
            <span className="eyebrow animate-fade-up">
              Entreprise Adaptée · Studio créatif inclusif
            </span>

            <h1
              className="mt-6 font-display font-extrabold leading-[1.1] tracking-tight text-[clamp(2.6rem,6.2vw,5.3rem)] animate-fade-up"
              style={{ animationDelay: '80ms' }}
            >
              Le studio de création
              <br className="hidden sm:block" />{' '}
              multimédia <span className="text-gradient">inclusif</span>
            </h1>

            <p
              className="mt-7 max-w-2xl text-[1.05rem] sm:text-xl text-[#cbbef0] leading-relaxed animate-fade-up"
              style={{ animationDelay: '160ms' }}
            >
              Accessibilité numérique, design &amp; animation 2D/3D, sites internet
              accessibles, sensibilisation / formation, communication FALC —
              imaginés par des talents aux <span className="text-white font-bold">« <span lang="en">Mad Skills</span> »</span>.
            </p>

            <div
              className="mt-9 flex flex-wrap items-center gap-4 animate-fade-up"
              style={{ animationDelay: '240ms' }}
            >
              <button onClick={scrollToCreations} className="btn btn-primary group">
                Découvrir nos créations
                <ArrowDown size={18} className="transition-transform group-hover:translate-y-0.5" />
              </button>
              <button onClick={openContact} className="btn btn-ghost group">
                Nous contacter
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>

            {/* Bandeau confiance / faits */}
            <div
              className="mt-12 flex flex-wrap gap-x-8 gap-y-4 text-sm text-[#a99bd0] animate-fade-up"
              style={{ animationDelay: '320ms' }}
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-violet-bright" /> Agrément Entreprise Adaptée
              </span>
              <span className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-violet-bright" /> Talents neuro-atypiques
              </span>
              <span className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-violet-bright" /> Réduction de la taxe AGEFIPH
              </span>
            </div>
          </div>

          {/* Colonne visuelle 3D (grands écrans) */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="relative animate-floaty">
              <TiltCard className="card-surface ring-gradient rounded-[1.8rem] p-3 shadow-glow" max={7}>
                <div className="relative overflow-hidden rounded-[1.4rem]">
                  <video
                    autoPlay={!reduced}
                    muted
                    loop
                    playsInline
                    aria-hidden="true"
                    className="h-[27rem] w-full object-cover"
                  >
                    <source src="/image place des victoires/turn_Marie.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0a0218]/90 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div>
                      <p className="font-display font-bold text-white text-lg leading-tight">Personnage 3D</p>
                      <p className="text-xs text-[#bcaee6]">Modélisé &amp; animé en interne</p>
                    </div>
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-grad-violet shadow-glow animate-pulse-ring">
                      <Play size={16} className="text-white translate-x-px" />
                    </span>
                  </div>
                </div>
              </TiltCard>

              {/* Puces technos flottantes */}
              <div className="absolute -top-5 -left-6 glass rounded-2xl px-4 py-2 text-sm font-semibold shadow-card animate-floaty" style={{ animationDelay: '1.2s' }}>
                Animation 2D/3D
              </div>
              <div className="absolute -bottom-6 -right-4 glass rounded-2xl px-4 py-2 text-sm font-semibold shadow-card animate-floaty" style={{ animationDelay: '0.6s' }}>
                Réalité augmentée
              </div>
            </div>
          </div>
        </div>

        {/* Marquee technos (mobile + desktop) */}
        <div className="mt-16 edge-fade overflow-hidden">
          <div className="flex w-max gap-3 animate-infinite-scroll">
            {[...chips, ...chips].map((c, i) => (
              <span
                key={i}
                className="whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-sm text-[#cbbef0]"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Indice de scroll */}
      <button
        onClick={scrollToCreations}
        aria-label="Faire défiler vers le bas"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-[#a99bd0] hover:text-white transition-colors"
      >
        <span className="text-xs tracking-[0.25em] uppercase">Défiler</span>
        <ArrowDown size={18} className="animate-floaty" />
      </button>

    </section>
  )
}

export default Hero
